import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios'
import { useAuthStore } from '@/stores/authStore'

// API Configuration
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://backend-production-c8a5.up.railway.app/api/v1'

// API Client Instance
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 2 minutos para sincronizações históricas
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request Interceptor - Add Auth Token
api.interceptors.request.use(
  (config) => {
    // Get token from store
    const { token, refreshToken, sessionId } = useAuthStore.getState()

    // Also try to get from localStorage as backup
    const storedAuth = localStorage.getItem('finpro-auth')
    let fallbackToken = null
    let fallbackRefreshToken = null

    if (storedAuth) {
      try {
        const parsed = JSON.parse(storedAuth)
        fallbackToken = parsed?.state?.token
        fallbackRefreshToken = parsed?.state?.refreshToken
      } catch (e) {
        console.warn('Error parsing stored auth:', e)
      }
    }

    const authToken = token || fallbackToken

    if (authToken) {
      // Clean the token - remove any whitespace or invisible characters
      const cleanToken = authToken.trim()
      config.headers.Authorization = `Bearer ${cleanToken}`

      console.log('🔐 Auth token sent:', cleanToken.substring(0, 20) + '...')
      console.log('🔍 Token length:', cleanToken.length)
      console.log('🔍 Full Authorization header:', `Bearer ${cleanToken}`)
      console.log('🔍 Request URL:', config.url)

      // Add session ID if available (for debugging)
      if (sessionId) {
        console.log('📝 Session ID:', sessionId.substring(0, 20) + '...')
      }
    } else {
      console.warn('⚠️ No auth token found for request:', config.url)
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response Interceptor - Handle Auth Errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // More intelligent 401 handling - only logout for authentication-related 401s
      const errorMessage = error.response?.data?.detail || ''
      const isAuthError = errorMessage.includes('Invalid') ||
                         errorMessage.includes('expired') ||
                         errorMessage.includes('token') ||
                         errorMessage.includes('unauthorized')

      console.warn('🚫 401 Unauthorized:', errorMessage)

      // Only logout if:
      // 1. Not already on login page
      // 2. We have a token stored
      // 3. Error is actually authentication related (not a business logic 401)
      if (!window.location.pathname.includes('/login') &&
          window.location.pathname !== '/' &&
          isAuthError) {
        const { token, refreshToken } = useAuthStore.getState()
        if (token || refreshToken) {
          console.warn('🔓 Logging out due to authentication error')
          useAuthStore.getState().logout()
          window.location.href = '/'
        }
      }
    }
    return Promise.reject(error)
  }
)

// API Response Types
export interface ApiResponse<T = any> {
  data: T
  message?: string
  status: string
}

export interface LoginRequest {
  email: string
  password: string
}

export interface LoginResponse {
  access_token: string
  token_type: string
  expires_in: number
  user: {
    id: string
    email: string
    full_name: string
    is_active: boolean
    created_at: string
  }
  tenant: {
    id: string
    name: string
    plan: string
  }
}

export interface RegisterRequest {
  email: string
  password: string
  full_name: string
  company_name: string
  cnpj?: string
  phone?: string
}

// Authentication API
export const authAPI = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/login', credentials)
    return response.data
  },

  register: async (data: RegisterRequest): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/register', data)
    return response.data
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout')
  },

  me: async (): Promise<LoginResponse['user']> => {
    const response = await api.get<LoginResponse['user']>('/auth/me')
    return response.data
  }
}

// Dashboard API
export interface DashboardMetrics {
  total_income: number
  total_expense: number
  balance: number
  net_flow: number
  transactions_count: number
  pending_bills: number
  accounts_count: number
  categories_count: number
  leads_count: number
  period: string
  total_balance: number
  monthly_growth: number
  monthly_income: number
  monthly_expenses: number
  investments_value: number
  expense_categories: any[]
  recent_transactions: any[]
}

export const dashboardAPI = {
  getMetrics: async (): Promise<DashboardMetrics> => {
    // Try unified financial dashboard first, fallback to old endpoint
    try {
      const unifiedData = await financeiroAPI.dashboard()
      return unifiedData as DashboardMetrics
    } catch (error) {
      console.warn('Unified dashboard endpoint failed, using legacy endpoint')
      const response = await api.get<DashboardMetrics>('/dashboard/metrics')
      return response.data
    }
  }
}

// Transactions API
export interface Transaction {
  id: string
  type: 'INCOME' | 'EXPENSE' | 'TRANSFER'
  amount: number
  description: string
  date: string
  category_id?: number
  account_id?: number
  status: 'PENDING' | 'COMPLETED' | 'CANCELLED'
  created_at: string
  updated_at: string
}

export interface CreateTransactionRequest {
  type: 'INCOME' | 'EXPENSE' | 'TRANSFER'
  amount: number
  description: string
  date: string
  category_id?: number
  account_id?: number
}

// Legacy Transactions API - now integrates with unified financial module
export const transactionsAPI = {
  list: async (params?: {
    page?: number,
    limit?: number,
    start_date?: string,
    end_date?: string,
    category_id?: string,
    account_id?: string,
    type?: string
  }): Promise<Transaction[]> => {
    // For now, keep using old endpoint until unified transactions endpoint is added
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)
    if (params?.category_id) queryParams.append('category_id', params.category_id)
    if (params?.account_id) queryParams.append('account_id', params.account_id)
    if (params?.type) queryParams.append('type', params.type)

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
    const response = await api.get<Transaction[]>(`/transactions${queryString}`)
    return response.data
  },

  getSummary: async (params?: {
    start_date?: string,
    end_date?: string,
    group_by?: 'day' | 'week' | 'month' | 'year'
  }): Promise<any> => {
    const queryParams = new URLSearchParams()
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)
    if (params?.group_by) queryParams.append('group_by', params.group_by)

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
    const response = await api.get(`/transactions/summary${queryString}`)
    return response.data
  },

  create: async (data: CreateTransactionRequest): Promise<Transaction> => {
    const response = await api.post<Transaction>('/transactions', data)
    return response.data
  },

  get: async (id: string): Promise<Transaction> => {
    const response = await api.get<Transaction>(`/transactions/${id}`)
    return response.data
  },

  update: async (id: string, data: Partial<CreateTransactionRequest>): Promise<Transaction> => {
    const response = await api.put<Transaction>(`/transactions/${id}`, data)
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/transactions/${id}`)
  }
}

// === UNIFIED FINANCIAL MODULE API ===
// Using the new unified financial endpoints from backend

export interface Category {
  id: number
  name: string
  type: 'INCOME' | 'EXPENSE'
  color: string
  icon: string
  description?: string
  is_active: boolean
  created_at?: string
}

export interface Bill {
  id: number
  type: string
  status: string
  description: string
  document_number?: string
  issue_date: string
  due_date: string
  payment_date?: string
  original_amount: number
  final_amount: number
  paid_amount: number
  balance_amount: number
  customer_id?: number
  supplier_id?: number
  category_id?: number
  is_overdue: boolean
  days_overdue: number
  created_at: string
  updated_at?: string
}

export interface CostCenter {
  id: number
  name: string
  code?: string
  parent_id?: number
  manager_name?: string
  manager_email?: string
  budget_monthly?: number
  budget_yearly?: number
  description?: string
  is_active: boolean
  created_at: string
}

export interface BankAccount {
  id: number
  name: string
  type: 'CHECKING' | 'SAVINGS' | 'CREDIT_CARD' | 'INVESTMENT'
  bank: string
  balance: number
  is_active: boolean
  is_main: boolean
  created_at: string
}

// Unified Financial API - replaces old separate APIs
export const financeiroAPI = {
  // Categories
  categorias: {
    list: async (): Promise<Category[]> => {
      const response = await api.get<any[]>('/financeiro/categorias')
      // Map Portuguese field names to English ones expected by frontend
      return response.data.map((cat: any) => ({
        id: cat.id,
        name: cat.nome,
        type: cat.tipo,
        color: cat.cor,
        icon: cat.icone || '📂', // Default icon if none provided
        description: cat.descricao || '',
        parent_category_id: cat.pai_id,
        is_active: true, // Assume active since only active categories are returned
        created_at: cat.created_at
      }))
    }
  },

  // Cost Centers
  centrosCusto: {
    list: async (): Promise<CostCenter[]> => {
      const response = await api.get<any[]>('/financeiro/centros-custo')
      // Map Portuguese field names to English ones expected by frontend
      return response.data.map((centro: any) => ({
        id: centro.id,
        name: centro.nome,
        code: centro.codigo,
        budget_monthly: centro.orcamento_mensal || 0,
        budget_yearly: (centro.orcamento_mensal || 0) * 12,
        is_active: true, // Assume active since only active centers are returned
        created_at: centro.created_at,
        description: centro.descricao || ''
      }))
    }
  },

  // Bank Accounts
  contasBancarias: {
    list: async (): Promise<BankAccount[]> => {
      const response = await api.get<BankAccount[]>('/financeiro/contas-bancarias')
      return response.data
    }
  },

  // Accounts Payable
  contasPagar: {
    list: async (params?: {
      page?: number
      limit?: number
      status?: string
      start_date?: string
      end_date?: string
    }): Promise<Bill[]> => {
      const queryParams = new URLSearchParams()
      if (params?.page) queryParams.append('page', params.page.toString())
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.status) queryParams.append('status', params.status)
      if (params?.start_date) queryParams.append('start_date', params.start_date)
      if (params?.end_date) queryParams.append('end_date', params.end_date)

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
      const response = await api.get<Bill[]>(`/financeiro/contas-pagar${queryString}`)
      return response.data
    },

    create: async (data: any): Promise<Bill> => {
      // Convert to backend format if needed
      const contaData = {
        fornecedor_id: data.supplier_id || data.fornecedor_id,
        numero_documento: data.document_number || data.numero_documento || '',
        descricao: data.description || data.descricao,
        valor: data.original_amount || data.valor_original || data.valor,
        conta_id: data.account_id || data.conta_id || 1,
        categoria_id: data.category_id || data.categoria_id || 1,
        centro_custo_id: data.cost_center_id || data.centro_custo_id,
        data_vencimento: data.due_date || data.data_vencimento,
        forma_pagamento: data.payment_method || data.metodo_pagamento || data.forma_pagamento,
        observacoes: data.notes || data.observacoes
      }

      console.log('DEBUG contasPagar.create: Dados sendo enviados:', JSON.stringify(contaData, null, 2))
      const response = await api.post<Bill>('/financeiro/contas-pagar', contaData)
      return response.data
    }
  },

  // Accounts Receivable
  contasReceber: {
    create: async (data: any): Promise<Bill> => {
      const response = await api.post<Bill>('/financeiro/contas-receber', data)
      return response.data
    }
  },

  // Sales (Vendas) - New complete sales endpoint with products
  vendas: {
    create: async (data: any): Promise<any> => {
      const response = await api.post('/financeiro/vendas', data)
      return response.data
    }
  },

  // Sellers/Affiliates (Vendedores)
  vendedores: {
    list: async (): Promise<any[]> => {
      const response = await api.get('/financeiro/vendedores')
      return response.data
    }
  },

  // Products (Produtos)
  produtos: {
    list: async (params?: {
      search?: string
      active_only?: boolean
      low_stock_only?: boolean
    }): Promise<any[]> => {
      const queryParams = new URLSearchParams()
      if (params?.search) queryParams.append('search', params.search)
      if (params?.active_only !== undefined) queryParams.append('active_only', params.active_only.toString())
      if (params?.low_stock_only) queryParams.append('low_stock_only', params.low_stock_only.toString())

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
      const response = await api.get(`/produtos${queryString}`)
      return response.data.produtos || response.data
    }
  },

  // Customers (Clientes)
  clientes: {
    list: async (): Promise<any[]> => {
      const response = await api.get('/financeiro/clientes')
      return response.data
    }
  },

  // Transfers
  transferencias: {
    create: async (data: any): Promise<any> => {
      const response = await api.post('/financeiro/transferencias', data)
      return response.data
    }
  },

  // Dashboard
  dashboard: async (): Promise<any> => {
    const response = await api.get('/financeiro/dashboard')
    return response.data
  }
}

// Invoice OCR API
export interface InvoiceUpload {
  id: number
  file_name: string
  file_type: string
  status: string
  message: string
}

export interface InvoiceDetail {
  id: number
  file_name: string
  file_type: string
  file_size?: number
  status: string
  processing_method?: string
  extracted_data?: any
  confidence_score?: number
  needs_review: boolean
  invoice_number?: string
  series?: string
  nfe_key?: string
  issue_date?: string
  total_amount?: string
  issuer_name?: string
  issuer_cnpj?: string
  bill_id?: number
  error_message?: string
  processing_duration_ms?: number
  created_at: string
  confirmed_at?: string
}

export interface InvoiceListItem {
  id: number
  file_name: string
  file_type: string
  status: string
  invoice_number?: string
  total_amount?: string
  issuer_name?: string
  confidence_score?: number
  needs_review: boolean
  created_at: string
}

export const invoicesAPI = {
  upload: async (file: File): Promise<InvoiceUpload> => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post<InvoiceUpload>('/invoices/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  get: async (invoiceId: number): Promise<InvoiceDetail> => {
    const response = await api.get<InvoiceDetail>(`/invoices/${invoiceId}`)
    return response.data
  },

  list: async (params?: { status?: string; limit?: number; offset?: number }): Promise<{
    invoices: InvoiceListItem[]
    total: number
    limit: number
    offset: number
  }> => {
    const queryParams = new URLSearchParams()
    if (params?.status) queryParams.append('status', params.status)
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.offset) queryParams.append('offset', params.offset.toString())
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
    const response = await api.get(`/invoices${queryString}`)
    return response.data
  },

  confirm: async (invoiceId: number, data: {
    due_date: string
    description?: string
    category_id?: number
    cost_center_id?: number
    project_id?: number
    notes?: string
  }): Promise<any> => {
    const response = await api.post(`/invoices/${invoiceId}/confirm`, data)
    return response.data
  },

  reject: async (invoiceId: number, reason: string): Promise<any> => {
    const response = await api.post(`/invoices/${invoiceId}/reject?reason=${encodeURIComponent(reason)}`)
    return response.data
  },

  delete: async (invoiceId: number): Promise<any> => {
    const response = await api.delete(`/invoices/${invoiceId}`)
    return response.data
  },

  preview: async (invoiceId: number): Promise<any> => {
    const response = await api.get(`/invoices/${invoiceId}/preview`)
    return response.data
  }
}

// Legacy Categories API - now uses unified endpoint
export const categoriesAPI = {
  list: async (): Promise<Category[]> => {
    return financeiroAPI.categorias.list()
  },

  get: async (id: string): Promise<Category> => {
    // Fallback to old endpoint if specific category needed
    const response = await api.get<Category>(`/categories/${id}`)
    return response.data
  },

  create: async (data: Omit<Category, 'id' | 'created_at'>): Promise<Category> => {
    // Fallback to old endpoint for CRUD operations
    const response = await api.post<Category>('/categories', data)
    return response.data
  },

  update: async (id: string, data: Partial<Category>): Promise<Category> => {
    const response = await api.put<Category>(`/categories/${id}`, data)
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/categories/${id}`)
  }
}

// Legacy Accounts API - now uses unified financial module
export interface Account {
  id: number
  name: string
  type: 'CHECKING' | 'SAVINGS' | 'CREDIT_CARD' | 'INVESTMENT'
  balance: number
  bank: string
  is_active: boolean
  is_main: boolean
  created_at: string
}

export const accountsAPI = {
  list: async (): Promise<Account[]> => {
    // Use unified financial module first, fallback to old endpoint
    try {
      const unifiedAccounts = await financeiroAPI.contasBancarias.list()
      // Map fields from Portuguese to English format
      return unifiedAccounts.map((conta: any) => ({
        id: conta.id,
        name: conta.nome || '',
        type: conta.tipo || 'CHECKING',
        bank: conta.banco || '',
        balance: conta.saldo || 0,
        is_primary: conta.is_primary || false,
        is_active: conta.is_active !== false,
        created_at: conta.created_at || conta.criado_em
      }))
    } catch (error) {
      console.warn('Unified accounts endpoint failed, using legacy endpoint')
      const response = await api.get<{accounts: Account[], total_balance: number, count: number}>('/accounts')
      return response.data.accounts
    }
  },

  get: async (id: string): Promise<Account> => {
    const response = await api.get<Account>(`/accounts/${id}`)
    return response.data
  },

  create: async (data: Omit<Account, 'id' | 'created_at' | 'balance'>): Promise<Account> => {
    const response = await api.post<Account>('/accounts', data)
    return response.data
  },

  update: async (id: string, data: Partial<Account>): Promise<Account> => {
    const response = await api.put<Account>(`/accounts/${id}`, data)
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/accounts/${id}`)
  },

  // Recalcular saldo baseado em transações
  correctBalance: async (accountId: number): Promise<any> => {
    const response = await api.patch(`/accounts/${accountId}/correct-balance`)
    return response.data
  },

  // Obter alertas de saldo baixo
  getLowBalanceAlerts: async (threshold: number = 100): Promise<any> => {
    const response = await api.get(`/accounts/alerts/low-balance?threshold=${threshold}`)
    return response.data
  },

  // Obter histórico de saldo
  getBalanceHistory: async (accountId: number, days: number = 30): Promise<any> => {
    const response = await api.get(`/accounts/${accountId}/balance-history?days=${days}`)
    return response.data
  },

  // Transferência entre contas (usa endpoint correto de transações)
  transfer: async (data: {
    from_account_id: number
    to_account_id: number
    amount: number
    date: string
    description?: string
    notes?: string
  }): Promise<any> => {
    const response = await api.post('/transactions/transfer', data)
    return response.data
  },

  // Obter transações de uma conta
  getTransactions: async (
    accountId: number,
    filters?: {
      start_date?: string
      end_date?: string
      transaction_type?: string
      limit?: number
      offset?: number
    }
  ): Promise<any> => {
    const params = new URLSearchParams()
    if (filters?.start_date) params.append('start_date', filters.start_date)
    if (filters?.end_date) params.append('end_date', filters.end_date)
    if (filters?.transaction_type) params.append('transaction_type', filters.transaction_type)
    if (filters?.limit) params.append('limit', filters.limit.toString())
    if (filters?.offset) params.append('offset', filters.offset.toString())

    const response = await api.get(`/accounts/${accountId}/transactions?${params.toString()}`)
    return response.data
  },

  // Reconciliação de conta
  reconcile: async (
    accountId: number,
    data: {
      reconciliation_date: string
      expected_balance: number
      notes?: string
    }
  ): Promise<any> => {
    const response = await api.post(`/accounts/${accountId}/reconcile`, data)
    return response.data
  },

  // Marcar transação como reconciliada
  markTransactionReconciled: async (transactionId: number, reconciled: boolean): Promise<any> => {
    const response = await api.patch(`/transactions/${transactionId}/reconcile`, { reconciled })
    return response.data
  }
}

// AI Services API
export interface AICategorizationRequest {
  description: string
  amount: number
  merchant?: string
}

export interface AICategorizationResponse {
  category: string
  subcategory: string
  confidence: number
  method: string
}

export interface FinancialHealthResponse {
  overall_score: number
  components: {
    cash_flow_stability: number
    expense_control: number
    revenue_diversification: number
    emergency_reserve: number
    growth_trend: number
  }
  recommendations: string[]
  risk_level: 'Low' | 'Medium' | 'High'
  analysis_date: string
}

export const aiAPI = {
  categorizeTransaction: async (data: AICategorizationRequest): Promise<AICategorizationResponse> => {
    const response = await api.post<AICategorizationResponse>('/ai/categorize/transaction', data)
    return response.data
  },

  getFinancialHealth: async (): Promise<FinancialHealthResponse> => {
    const response = await api.get<FinancialHealthResponse>('/ai/insights/financial-health')
    return response.data
  },

  getInsights: async (): Promise<any> => {
    // Backend has consolidated insights endpoint
    const response = await api.get('/ai/insights')
    return response.data
  },

  getCashFlowPredictions: async (daysAhead: number = 30): Promise<any> => {
    // Updated to use GET endpoint with real database data
    const response = await api.get(`/ai/predictions/cash-flow?days_ahead=${daysAhead}`)
    return response.data
  },

  getMonthlySummaryPrediction: async (): Promise<any> => {
    // Updated to use GET endpoint with real database data
    const response = await api.get('/ai/predictions/next-month')
    return response.data
  },

  getCategorySuggestions: async (description: string): Promise<any> => {
    const response = await api.get(`/ai/categorize/suggestions?description=${encodeURIComponent(description)}`)
    return response.data
  },

  // Auditoria inteligente
  auditTransactions: async (params?: { start_date?: string; end_date?: string; limit?: number }): Promise<any> => {
    console.log('🔧 [api.ts] auditTransactions CHAMADA com params:', params)

    const queryParams = new URLSearchParams()
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)
    if (params?.limit) queryParams.append('limit', params.limit.toString())

    const url = `/ai/audit/transactions${queryParams.toString() ? '?' + queryParams.toString() : ''}`
    console.log('🔧 [api.ts] URL gerada:', url)

    const response = await api.get(url)
    console.log('🔧 [api.ts] Resposta recebida:', response.data)

    return response.data
  },

  applyAuditCorrections: async (corrections: Array<{ transaction_id: number; new_category: string; new_subcategory: string }>): Promise<any> => {
    const response = await api.post('/ai/audit/apply-corrections', corrections)
    return response.data
  }
}

// CRM API
export interface Lead {
  id: number
  name: string
  email: string
  phone?: string
  company?: string
  position?: string
  source: string
  status: string
  score: number
  estimated_value: number
  created_at: string
  last_contact: string
}

export interface CRMStats {
  total_leads: number
  hot_leads: number
  cold_leads: number
  converted_leads: number
  conversion_rate: number
  average_score: number
  total_pipeline_value: number
}

// Customer interfaces (Customer Hub)
export interface Customer {
  id: number
  name: string
  email: string
  phone?: string
  company?: string
  document?: string
  status: string
  customer_type: string
  lifetime_value: number
  total_revenue: number
  total_orders: number
  average_ticket: number
  health_score: number
  risk_score: number
  is_vip: boolean
  is_at_risk: boolean
  customer_rank: number
  supplier_rank: number
  business_roles: string[]
  primary_role: string
  is_customer: boolean
  is_supplier: boolean
  is_both: boolean
  created_at: string
  last_purchase_date?: string
}

export interface CustomerTimeline {
  type: 'bill' | 'interaction' | 'opportunity' | 'project'
  date: string
  title: string
  description?: string
  amount?: number
  status?: string
  data: any
}

export interface CustomerStats {
  customer: Customer
  bills: {
    total_open: number
    total_overdue: number
    count_open: number
    count_paid: number
    count_overdue: number
  }
  opportunities: {
    total_pipeline: number
    total_won: number
    count_open: number
    count_won: number
  }
  metrics: {
    lifetime_value: number
    total_revenue: number
    total_orders: number
    average_ticket: number
    health_score: number
    risk_score: number
    days_since_last_purchase: number
    is_vip: boolean
    is_at_risk: boolean
  }
  ranking: {
    customer_rank: number
    supplier_rank: number
    business_roles: string[]
    primary_role: string
    is_customer: boolean
    is_supplier: boolean
    is_both: boolean
  }
}

export interface CustomersAnalytics {
  summary: {
    total_customers: number
    active_customers: number
    only_customers: number
    only_suppliers: number
    both_roles: number
    prospects: number
    at_risk: number
  }
  financial: {
    total_ltv: number
    average_health_score: number
  }
  top_customers: Array<{
    id: number
    name: string
    company?: string
    lifetime_value: number
    total_orders: number
    primary_role: string
  }>
}

export const crmAPI = {
  // ===== LEADS =====
  getLeads: async (): Promise<Lead[]> => {
    try {
      const response = await api.get<Lead[]>('/crm/leads')
      return response.data
    } catch (error) {
      console.warn('CRM leads endpoint not available, using mock data')
      return []
    }
  },

  getStats: async (): Promise<CRMStats> => {
    try {
      const leadsData = await crmAPI.getLeads()

      const stats: CRMStats = {
        total_leads: leadsData.length,
        hot_leads: leadsData.filter((lead: any) => lead.status === 'hot').length,
        cold_leads: leadsData.filter((lead: any) => lead.status === 'cold').length,
        converted_leads: leadsData.filter((lead: any) => lead.status === 'converted').length,
        conversion_rate: leadsData.length > 0 ?
          (leadsData.filter((lead: any) => lead.status === 'converted').length / leadsData.length) * 100 : 0,
        average_score: leadsData.length > 0 ?
          leadsData.reduce((sum: number, lead: any) => sum + (lead.score || 0), 0) / leadsData.length : 0,
        total_pipeline_value: leadsData.reduce((sum: number, lead: any) => sum + (lead.estimated_value || 0), 0)
      }

      return stats
    } catch (error) {
      console.warn('CRM not available, using mock stats')
      return {
        total_leads: 0,
        hot_leads: 0,
        cold_leads: 0,
        converted_leads: 0,
        conversion_rate: 0,
        average_score: 0,
        total_pipeline_value: 0
      }
    }
  },

  createLead: async (data: Omit<Lead, 'id' | 'created_at' | 'score'>): Promise<Lead> => {
    const response = await api.post<Lead>('/crm/leads', data)
    return response.data
  },

  updateLead: async (id: number, data: Partial<Lead>): Promise<Lead> => {
    const response = await api.put<Lead>(`/crm/leads/${id}`, data)
    return response.data
  },

  deleteLead: async (id: number): Promise<void> => {
    await api.delete(`/crm/leads/${id}`)
  },

  // ===== CUSTOMERS (Customer Hub) =====
  getCustomers: async (params?: {
    status?: string
    role?: string // 'customer', 'supplier', 'both', 'prospect'
    search?: string
    skip?: number
    limit?: number
  }): Promise<{ customers: Customer[]; total_count: number }> => {
    const response = await api.get('/crm/customers', { params })
    return response.data
  },

  getCustomer: async (id: number): Promise<Customer> => {
    const response = await api.get(`/crm/customers/${id}`)
    return response.data
  },

  createCustomer: async (data: Partial<Customer>): Promise<Customer> => {
    const response = await api.post('/crm/customers', data)
    return response.data
  },

  updateCustomer: async (id: number, data: Partial<Customer>): Promise<Customer> => {
    const response = await api.put(`/crm/customers/${id}`, data)
    return response.data
  },

  getCustomerTimeline: async (id: number, limit?: number): Promise<{
    customer_id: number
    customer_name: string
    timeline: CustomerTimeline[]
    total_events: number
  }> => {
    const response = await api.get(`/crm/customers/${id}/timeline`, {
      params: { limit }
    })
    return response.data
  },

  getCustomerStats: async (id: number): Promise<CustomerStats> => {
    const response = await api.get(`/crm/customers/${id}/stats`)
    return response.data
  },

  recalculateCustomerMetrics: async (id: number): Promise<{
    message: string
    customer_id: number
    metrics: any
  }> => {
    const response = await api.post(`/crm/customers/${id}/recalculate-metrics`)
    return response.data
  },

  getCustomersAnalytics: async (): Promise<CustomersAnalytics> => {
    const response = await api.get('/crm/customers/analytics/overview')
    return response.data
  }
}

// Crypto API
export interface CryptoWallet {
  id: number
  name: string
  wallet_type: string
  currency_code: string
  network?: string
  public_address?: string
  balance?: number
  available_balance?: number
  fiat_value?: number
  fiat_currency?: string
  is_active: boolean
  created_at?: string
  updated_at?: string
}

export interface CryptoHolding {
  id: number
  symbol: string
  amount: number
  current_price?: number
  current_value_usd?: number
  current_value_brl?: number
  unrealized_pnl_usd?: number
  // Keep these for backward compatibility with existing frontend code
  value_usd?: number
  value_brl?: number
  change_24h?: number
  change_percentage_24h?: number
  name?: string
  wallet_id?: string
  average_price?: number
}

export interface CryptoStats {
  total_value_usd: number
  total_value_brl: number
  total_change_24h: number
  total_change_percentage_24h: number
  top_performers: CryptoHolding[]
  worst_performers: CryptoHolding[]
}

export const cryptoAPI = {
  getWallets: async (): Promise<CryptoWallet[]> => {
    try {
      const response = await api.get<CryptoWallet[]>('/crypto/wallets')
      return response.data || []
    } catch (error) {
      console.warn('Crypto wallets endpoint not available:', error)
      return []
    }
  },

  getHoldings: async (): Promise<CryptoHolding[]> => {
    try {
      const response = await api.get<CryptoHolding[]>('/crypto/holdings')
      return response.data || []
    } catch (error) {
      console.warn('Crypto holdings endpoint not available:', error)
      return []
    }
  },

  getStats: async (): Promise<CryptoStats> => {
    try {
      // Use the portfolio summary endpoint that exists in backend
      const response = await api.get('/crypto/portfolio/summary')

      // Backend response structure - exact mapping
      const data = response.data
      console.log('Crypto portfolio summary data:', data) // Debug log

      return {
        total_value_usd: data.total_value_usd || 0,
        total_value_brl: data.total_value_brl || 0,
        total_change_24h: 0, // Not provided by backend
        total_change_percentage_24h: 0, // Not provided by backend
        top_performers: data.holdings ? data.holdings.slice(0, 3) : [],
        worst_performers: data.holdings ? data.holdings.slice(-3) : []
      }
    } catch (error) {
      console.warn('Crypto portfolio summary not available, calculating from holdings')

      try {
        // Fallback - calculate from holdings if portfolio summary fails
        const holdings = await cryptoAPI.getHoldings()

        const stats: CryptoStats = {
          total_value_usd: holdings.reduce((sum: number, h: any) => sum + (h.current_value_usd || h.value_usd || 0), 0),
          total_value_brl: holdings.reduce((sum: number, h: any) => sum + (h.current_value_brl || h.value_brl || 0), 0),
          total_change_24h: holdings.reduce((sum: number, h: any) => sum + (h.change_24h || 0), 0),
          total_change_percentage_24h: holdings.length > 0 ?
            holdings.reduce((sum: number, h: any) => sum + (h.change_percentage_24h || 0), 0) / holdings.length : 0,
          top_performers: holdings
            .filter((h: any) => (h.change_percentage_24h || 0) > 0)
            .sort((a: any, b: any) => (b.change_percentage_24h || 0) - (a.change_percentage_24h || 0))
            .slice(0, 3),
          worst_performers: holdings
            .filter((h: any) => (h.change_percentage_24h || 0) < 0)
            .sort((a: any, b: any) => (a.change_percentage_24h || 0) - (b.change_percentage_24h || 0))
            .slice(0, 3)
        }

        return stats
      } catch (holdingsError) {
        console.warn('Crypto holdings not available, returning empty stats')
        return {
          total_value_usd: 0,
          total_value_brl: 0,
          total_change_24h: 0,
          total_change_percentage_24h: 0,
          top_performers: [],
          worst_performers: []
        }
      }
    }
  },

  getPrices: async (symbols?: string[]): Promise<any> => {
    const params = symbols ? `?symbols=${symbols.join(',')}` : '?symbols=BTC,ETH,USDT,BNB'
    const response = await api.get(`/crypto/prices/current${params}`)
    return response.data
  },

  createWallet: async (data: Omit<CryptoWallet, 'id' | 'created_at' | 'balance'>): Promise<CryptoWallet> => {
    const response = await api.post<CryptoWallet>('/crypto/wallets', data)
    return response.data
  },

  updateWallet: async (id: string, data: Partial<CryptoWallet>): Promise<CryptoWallet> => {
    const response = await api.put<CryptoWallet>(`/crypto/wallets/${id}`, data)
    return response.data
  },

  deleteWallet: async (id: string): Promise<void> => {
    await api.delete(`/crypto/wallets/${id}`)
  },

  syncWallet: async (walletId: string): Promise<void> => {
    await api.post(`/crypto/wallets/${walletId}/sync`)
  }
}

// Payment Gateways API
export interface PaymentGateway {
  id: string
  name: string
  type: 'asaas' | 'pagbank' | 'stripe' | 'mercadopago'
  status: 'active' | 'inactive' | 'error'
  is_default: boolean
  fees: {
    credit_card: number
    debit_card: number
    pix: number
    boleto: number
  }
  settings: {
    api_key?: string
    environment: 'sandbox' | 'production'
    webhook_url?: string
  }
  last_sync: string
  monthly_volume: number
  monthly_transactions: number
}

export interface PaymentStats {
  total_processed_today: number
  total_volume_month: number
  successful_payments: number
  failed_payments: number
  pending_payments: number
  average_ticket: number
  top_gateway: string
  pix_percentage: number
  card_percentage: number
  boleto_percentage: number
}

export interface PaymentTransaction {
  id: string
  amount: number
  method: 'pix' | 'credit_card' | 'debit_card' | 'boleto'
  status: 'success' | 'pending' | 'failed'
  gateway: string
  customer_name: string
  created_at: string
  transaction_id: string
}

export const paymentGatewayAPI = {
  getGateways: async (): Promise<PaymentGateway[]> => {
    const response = await api.get<PaymentGateway[]>('/payment-gateways/')
    return response.data
  },

  getGateway: async (id: string): Promise<PaymentGateway> => {
    const response = await api.get<PaymentGateway>(`/payment-gateways/${id}`)
    return response.data
  },

  getSupportedGateways: async () => {
    const response = await api.get('/payment-gateways/supported')
    return response.data
  },

  getStats: async (): Promise<PaymentStats> => {
    const response = await api.get<PaymentStats>('/payment-gateways/stats')
    return response.data
  },

  getTransactions: async (): Promise<PaymentTransaction[]> => {
    const response = await api.get<PaymentTransaction[]>('/payment-gateways/transactions/recent')
    return response.data
  },

  testConnection: async (data: {
    gateway_type: string
    gateway_name: string
    api_key: string
    api_secret?: string
    environment: string
    webhook_secret?: string
    gateway_config?: any
  }): Promise<{ success: boolean; message: string; details?: any }> => {
    const response = await api.post('/payment-gateways/test-connection', data)
    return response.data
  },

  create: async (data: {
    gateway_type: string
    gateway_name: string
    api_key: string
    api_secret?: string
    environment: string
    webhook_secret?: string
    gateway_config?: any
    is_active: boolean
  }): Promise<PaymentGateway> => {
    const response = await api.post<PaymentGateway>('/payment-gateways/', data)
    return response.data
  },

  createGateway: async (data: Omit<PaymentGateway, 'id' | 'last_sync' | 'monthly_volume' | 'monthly_transactions'>): Promise<PaymentGateway> => {
    const response = await api.post<PaymentGateway>('/payment-gateways/', data)
    return response.data
  },

  updateGateway: async (id: string, data: Partial<PaymentGateway>): Promise<PaymentGateway> => {
    const response = await api.put<PaymentGateway>(`/payment-gateways/${id}`, data)
    return response.data
  },

  testGateway: async (id: string): Promise<any> => {
    const response = await api.post(`/payment-gateways/${id}/test-connection`)
    return response.data
  },

  syncGateway: async (id: string, syncType: string = 'all', forceFullSync: boolean = false): Promise<any> => {
    const params = new URLSearchParams()
    params.append('sync_type', syncType)
    params.append('force_full_sync', forceFullSync.toString())
    const response = await api.post(`/payment-gateways/${id}/sync?${params.toString()}`)
    return response.data
  },

  // Sincronizar gateway com sistema bancário - cria conta bancária automaticamente
  syncGatewayToBank: async (id: string): Promise<{
    success: boolean
    message: string
    bank_account_id: number
    transactions_synced: number
    current_balance: number
  }> => {
    console.log('🔧 API Client: syncGatewayToBank chamado com ID:', id)
    console.log('🔧 URL:', `/payment-gateways/${id}/sync-to-bank`)
    const response = await api.post(`/payment-gateways/${id}/sync-to-bank`)
    console.log('🔧 API Response (raw):', response.data)
    console.log('🔧 API Response (stringified):', JSON.stringify(response.data, null, 2))
    return response.data
  },

  // Buscar saldo em tempo real do gateway
  getGatewayBalance: async (id: string): Promise<{
    gateway_id: number
    balance: number
    available_balance: number
    currency: string
  }> => {
    const response = await api.get(`/payment-gateways/${id}/balance`)
    return response.data
  },

  // Buscar transações em tempo real do gateway
  getGatewayTransactions: async (id: string, days: number = 30, limit: number = 100): Promise<{
    transactions: any[]
    count: number
  }> => {
    const response = await api.get(`/payment-gateways/${id}/transactions?days=${days}&limit=${limit}`)
    return response.data
  },

  // Deletar gateway de pagamento
  deleteGateway: async (id: string): Promise<void> => {
    await api.delete(`/payment-gateways/${id}`)
  },

  // ===== CRIAR PEDIDOS (ORDERS) =====

  // Criar pedido PIX
  createOrderPix: async (gatewayId: string, data: {
    amount: number // em centavos
    customer_name: string
    customer_email: string
    customer_document: string
    customer_type?: string
    customer_phone_country?: string
    customer_phone_area: string
    customer_phone_number: string
    customer_address_line: string
    customer_address_zip: string
    customer_address_city: string
    customer_address_state: string
    customer_address_country?: string
    items: Array<{
      code?: string
      description: string
      amount: number // valor unitário em centavos
      quantity: number
    }>
    expires_in?: number // segundos até expirar (padrão 86400 = 24h)
  }): Promise<{
    success: boolean
    order_id: string
    pix_qr_code: string
    pix_qr_code_url: string
    pix_code: string
    expires_at: string
    status: string
  }> => {
    const response = await api.post(`/payment-gateways/${gatewayId}/orders/pix`, data)
    return response.data
  },

  // Criar pedido Cartão de Crédito
  createOrderCreditCard: async (gatewayId: string, data: {
    amount: number // em centavos
    customer_name: string
    customer_email: string
    customer_document: string
    customer_type?: string
    customer_phone_country?: string
    customer_phone_area: string
    customer_phone_number: string
    customer_address_line: string
    customer_address_zip: string
    customer_address_city: string
    customer_address_state: string
    customer_address_country?: string
    items: Array<{
      code?: string
      description: string
      amount: number
      quantity: number
    }>
    card_id?: string // Cartão salvo
    card_token?: string // Token (recomendado)
    card_number?: string // Dados crus (não recomendado)
    card_holder_name?: string
    card_exp_month?: string
    card_exp_year?: string
    card_cvv?: string
    installments?: number
    statement_descriptor?: string
    capture?: boolean
  }): Promise<{
    success: boolean
    order_id: string
    status: string
    amount: number
    installments: number
  }> => {
    const response = await api.post(`/payment-gateways/${gatewayId}/orders/credit-card`, data)
    return response.data
  },

  // Criar pedido Boleto
  createOrderBoleto: async (gatewayId: string, data: {
    amount: number // em centavos
    customer_name: string
    customer_email: string
    customer_document: string
    customer_type?: string
    customer_phone_country?: string
    customer_phone_area: string
    customer_phone_number: string
    customer_address_line: string
    customer_address_number: string
    customer_address_zip: string
    customer_address_city: string
    customer_address_state: string
    customer_address_country?: string
    items: Array<{
      code?: string
      description: string
      amount: number
      quantity: number
    }>
    due_date?: string // YYYY-MM-DD
    instructions?: string
  }): Promise<{
    success: boolean
    order_id: string
    boleto_url: string
    boleto_barcode: string
    boleto_linha_digitavel: string
    due_date: string
    status: string
  }> => {
    const response = await api.post(`/payment-gateways/${gatewayId}/orders/boleto`, data)
    return response.data
  }
}

// Accounting API
export interface AccountingAccount {
  id: number
  code: string
  name: string
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'REVENUE' | 'EXPENSE'
  balance: number
  parent_id?: number
  is_active: boolean
  created_at: string
}

export interface JournalEntry {
  id: number
  date: string
  description: string
  reference?: string
  total_amount: number
  created_at: string
  entries: Array<{
    id: number
    account_id: number
    account_name: string
    debit: number
    credit: number
  }>
}

export interface AccountingStats {
  total_assets: number
  total_liabilities: number
  total_equity: number
  total_revenue: number
  total_expenses: number
  net_income: number
  accounts_count: number
  journal_entries_count: number
}

export interface BalanceSheet {
  assets: {
    current_assets: Array<{ account: string, balance: number }>
    non_current_assets: Array<{ account: string, balance: number }>
    total_assets: number
  }
  liabilities: {
    current_liabilities: Array<{ account: string, balance: number }>
    non_current_liabilities: Array<{ account: string, balance: number }>
    total_liabilities: number
  }
  equity: {
    equity_accounts: Array<{ account: string, balance: number }>
    share_capital: number
    retained_earnings: number
    current_year_result: number
    total_equity: number
  }
  reference_date: string
}

export interface IncomeStatement {
  revenue: Array<{ account: string, amount: number }>
  expenses: Array<{ account: string, amount: number }>
  total_revenue: number
  total_expenses: number
  gross_profit: number
  net_income: number
  period: {
    start_date: string
    end_date: string
  }
}

export const accountingAPI = {
  getAccounts: async (): Promise<AccountingAccount[]> => {
    try {
      const response = await api.get<any>('/accounting/accounts')
      if (response.data && response.data.success && response.data.accounts) {
        return response.data.accounts
      }
      return response.data || []
    } catch (error) {
      console.warn('Accounting accounts endpoint not available, returning empty array')
      return []
    }
  },

  getStats: async (): Promise<AccountingStats> => {
    try {
      // Try to get data from balance sheet and income statement instead
      const today = new Date().toISOString().split('T')[0]
      const [balanceSheet, incomeStatement, accounts] = await Promise.all([
        accountingAPI.getBalanceSheet(today),
        accountingAPI.getIncomeStatement(),
        accountingAPI.getAccounts()
      ])

      const stats: AccountingStats = {
        total_assets: balanceSheet.assets.total_assets,
        total_liabilities: balanceSheet.liabilities.total_liabilities,
        total_equity: balanceSheet.equity.total_equity,
        total_revenue: incomeStatement.total_revenue,
        total_expenses: incomeStatement.total_expenses,
        net_income: incomeStatement.net_income,
        accounts_count: accounts.length,
        journal_entries_count: 0 // Would need to get from journal entries endpoint
      }

      return stats
    } catch (error) {
      console.warn('Accounting endpoints not available, using basic calculation from accounts')

      // Fallback to accounts-based calculation
      try {
        const accounts = await api.get('/accounting/accounts')
        const accountsData = accounts.data || []

        const stats: AccountingStats = {
          total_assets: accountsData
            .filter((a: any) => a.type === 'ASSET')
            .reduce((sum: number, a: any) => sum + (a.balance || 0), 0),
          total_liabilities: accountsData
            .filter((a: any) => a.type === 'LIABILITY')
            .reduce((sum: number, a: any) => sum + (a.balance || 0), 0),
          total_equity: accountsData
            .filter((a: any) => a.type === 'EQUITY')
            .reduce((sum: number, a: any) => sum + (a.balance || 0), 0),
          total_revenue: accountsData
            .filter((a: any) => a.type === 'REVENUE')
            .reduce((sum: number, a: any) => sum + (a.balance || 0), 0),
          total_expenses: accountsData
            .filter((a: any) => a.type === 'EXPENSE')
            .reduce((sum: number, a: any) => sum + (a.balance || 0), 0),
          net_income: 0,
          accounts_count: accountsData.length,
          journal_entries_count: 0
        }

        stats.net_income = stats.total_revenue - stats.total_expenses
        return stats
      } catch (accountsError) {
        // Return empty stats if everything fails
        return {
          total_assets: 0,
          total_liabilities: 0,
          total_equity: 0,
          total_revenue: 0,
          total_expenses: 0,
          net_income: 0,
          accounts_count: 0,
          journal_entries_count: 0
        }
      }
    }
  },

  getJournalEntries: async (): Promise<JournalEntry[]> => {
    const response = await api.get<JournalEntry[]>('/accounting/journal-entries')
    return response.data
  },

  getBalanceSheet: async (referenceDate?: string): Promise<BalanceSheet> => {
    try {
      // Always pass reference_date - use provided date or current date as default
      const defaultDate = referenceDate || new Date().toISOString().split('T')[0] // YYYY-MM-DD format
      const params = `?reference_date=${defaultDate}`
      const response = await api.get<any>(`/accounting/balance-sheet${params}`)

      if (response.data && response.data.success) {
        // Transform backend response to match frontend interface
        const data = response.data
        return {
          assets: {
            current_assets: data.assets?.current_assets || [],
            non_current_assets: data.assets?.non_current_assets || [],
            total_assets: data.assets?.total_assets || 0
          },
          liabilities: {
            current_liabilities: data.liabilities?.current_liabilities || [],
            non_current_liabilities: data.liabilities?.non_current_liabilities || [],
            total_liabilities: data.liabilities?.total_liabilities || 0
          },
          equity: {
            equity_accounts: data.equity?.equity_accounts || [],
            share_capital: data.equity?.share_capital || 0,
            retained_earnings: data.equity?.retained_earnings || 0,
            current_year_result: data.equity?.current_year_result || 0,
            total_equity: data.equity?.total_equity || 0
          },
          reference_date: defaultDate
        }
      }

      // Fallback balance sheet
      return {
        assets: {
          current_assets: [],
          non_current_assets: [],
          total_assets: 0
        },
        liabilities: {
          current_liabilities: [],
          non_current_liabilities: [],
          total_liabilities: 0
        },
        equity: {
          equity_accounts: [],
          share_capital: 0,
          retained_earnings: 0,
          current_year_result: 0,
          total_equity: 0
        },
        reference_date: defaultDate
      }
    } catch (error) {
      console.warn('Balance sheet endpoint not available, returning default data')
      const defaultDate = referenceDate || new Date().toISOString().split('T')[0]
      return {
        assets: {
          current_assets: [],
          non_current_assets: [],
          total_assets: 0
        },
        liabilities: {
          current_liabilities: [],
          non_current_liabilities: [],
          total_liabilities: 0
        },
        equity: {
          equity_accounts: [],
          share_capital: 0,
          retained_earnings: 0,
          current_year_result: 0,
          total_equity: 0
        },
        reference_date: defaultDate
      }
    }
  },

  getIncomeStatement: async (startDate?: string, endDate?: string): Promise<IncomeStatement> => {
    try {
      // DRE endpoint requires start_date and end_date parameters
      const currentYear = new Date().getFullYear()
      const defaultStartDate = startDate || `${currentYear}-01-01`
      const defaultEndDate = endDate || new Date().toISOString().split('T')[0]

      const params = new URLSearchParams()
      params.append('start_date', defaultStartDate)
      params.append('end_date', defaultEndDate)

      const response = await api.get<any>(`/accounting/dre?${params.toString()}`)

      if (response.data && response.data.success) {
        // Transform backend response to match frontend interface
        const data = response.data
        return {
          revenue: data.revenue?.items || [],
          expenses: data.expenses?.items || [],
          total_revenue: data.revenue?.total_revenue || 0,
          total_expenses: data.expenses?.total_expenses || 0,
          gross_profit: data.results?.gross_result || 0,
          net_income: data.results?.net_result || 0,
          period: {
            start_date: defaultStartDate,
            end_date: defaultEndDate
          }
        }
      }

      // Fallback if no data
      return {
        revenue: [],
        expenses: [],
        total_revenue: 0,
        total_expenses: 0,
        gross_profit: 0,
        net_income: 0,
        period: {
          start_date: defaultStartDate,
          end_date: defaultEndDate
        }
      }
    } catch (error) {
      console.warn('Income statement endpoint not available, returning default data')
      const currentYear = new Date().getFullYear()
      return {
        revenue: [],
        expenses: [],
        total_revenue: 0,
        total_expenses: 0,
        gross_profit: 0,
        net_income: 0,
        period: {
          start_date: `${currentYear}-01-01`,
          end_date: new Date().toISOString().split('T')[0]
        }
      }
    }
  },

  createAccount: async (data: Omit<AccountingAccount, 'id' | 'balance' | 'created_at'>): Promise<AccountingAccount> => {
    const response = await api.post<AccountingAccount>('/accounting/accounts', data)
    return response.data
  },

  createJournalEntry: async (data: {
    date: string
    description: string
    reference?: string
    entries: Array<{
      account_id: number
      debit: number
      credit: number
    }>
  }): Promise<JournalEntry> => {
    const response = await api.post<JournalEntry>('/accounting/journal-entries', data)
    return response.data
  }
}

// Bills API
export interface Bill {
  id: number
  type: string
  status: string
  description: string
  document_number?: string
  issue_date: string
  due_date: string
  payment_date?: string
  original_amount: string
  discount_amount: string
  interest_amount: string
  fine_amount: string
  paid_amount: string
  final_amount: string
  balance_amount: string
  customer_id?: number
  supplier_id?: number
  category_id?: number
  is_installment: boolean
  installment_number?: number
  total_installments?: number
  payment_method?: string
  notes?: string
  is_overdue: boolean
  days_overdue: number
  created_at: string
  updated_at: string
}

export interface BillStats {
  total_pending: number
  total_overdue: number
  total_paid_this_month: number
  bills_due_this_week: number
  average_bill_amount: number
  largest_pending_bill: number
  suppliers_count: number
  pending_amount: number
  overdue_amount: number
}

export interface Supplier {
  id: number
  name: string
  document?: string
  email?: string
  phone?: string
  address?: string
  payment_terms_days: number
  is_active: boolean
}

export const billsAPI = {
  getBills: async (): Promise<Bill[]> => {
    try {
      const response = await api.get<any[]>('/financeiro/contas-pagar')

      // Convert backend conta_pagar format to frontend Bill interface
      const bills: Bill[] = response.data.map((conta: any) => ({
        id: conta.id,
        type: conta.tipo || 'ACCOUNTS_PAYABLE',
        status: conta.status,
        description: conta.descricao,
        issue_date: conta.created_at,
        due_date: conta.data_vencimento,
        payment_date: conta.data_pagamento,
        original_amount: parseFloat(conta.valor) || 0,
        final_amount: parseFloat(conta.valor) || 0,
        paid_amount: conta.data_pagamento ? parseFloat(conta.valor) || 0 : 0,
        balance_amount: conta.data_pagamento ? 0 : parseFloat(conta.valor) || 0,
        category_id: conta.categoria?.id,
        supplier_id: conta.conta?.id,
        is_overdue: conta.status === 'VENCIDO',
        days_overdue: 0,
        created_at: conta.created_at,
        updated_at: conta.updated_at
      }))

      return bills
    } catch (error) {
      console.error('Error fetching bills from financeiro API:', error)
      // Fallback to mock data
      return []
    }
  },

  getStats: async (): Promise<BillStats> => {
    try {
      const response = await api.get('/financeiro/contas-pagar/estatisticas')

      // Convert backend stats to frontend format
      return {
        total_pending: response.data.total_pendentes || 0,
        total_overdue: response.data.total_vencidas || 0,
        total_paid_this_month: response.data.total_pagas_mes || 0,
        bills_due_this_week: response.data.vencendo_semana || 0,
        average_bill_amount: parseFloat(response.data.valor_medio || 0),
        largest_pending_bill: parseFloat(response.data.maior_conta_pendente || 0),
        suppliers_count: response.data.total_fornecedores || 0,
        pending_amount: parseFloat(response.data.valor_total_pendente || 0),
        overdue_amount: parseFloat(response.data.valor_total_vencido || 0)
      }
    } catch (error) {
      console.error('Error fetching bill stats from financeiro API:', error)
      // Fallback - calculate stats from bills
      const bills = await billsAPI.getBills()
      return {
        total_pending: bills.filter(b => b.status === 'pending').length,
        total_overdue: bills.filter(b => b.status === 'overdue').length,
        total_paid_this_month: bills.filter(b => b.status === 'paid').length,
        bills_due_this_week: 0,
        average_bill_amount: bills.length > 0 ? bills.reduce((sum, b) => sum + b.original_amount, 0) / bills.length : 0,
        largest_pending_bill: bills.filter(b => b.status === 'pending').length > 0 ?
          Math.max(...bills.filter(b => b.status === 'pending').map(b => b.original_amount)) : 0,
        suppliers_count: new Set(bills.map(b => b.supplier_id).filter(Boolean)).size,
        pending_amount: bills.filter(b => b.status === 'pending').reduce((sum, b) => sum + b.balance_amount, 0),
        overdue_amount: bills.filter(b => b.status === 'overdue').reduce((sum, b) => sum + b.balance_amount, 0)
      }
    }
  },

  getSuppliers: async (): Promise<Supplier[]> => {
    try {
      const response = await api.get<Supplier[]>('/financeiro/fornecedores')
      return response.data
    } catch (error) {
      console.error('Error fetching suppliers from financeiro API:', error)
      return []
    }
  },
  createSupplier: async (data: any): Promise<Supplier> => {
    try {
      const response = await api.post<Supplier>('/financeiro/fornecedores', data)
      return response.data
    } catch (error) {
      console.error('Error creating supplier:', error)
      throw error
    }
  },

  createBill: async (data: Omit<Bill, 'id' | 'created_at' | 'supplier_name' | 'category_name'>): Promise<Bill> => {
    try {
      // Convert frontend Bill format to backend conta_pagar format
      const contaData = {
        fornecedor_id: data.supplier_id,
        numero_documento: data.document_number,
        descricao: data.description,
        valor: data.original_amount,
        conta_id: data.account_id || 1, // Default account if not provided
        categoria_id: data.category_id || 1, // Default category if not provided
        centro_custo_id: data.cost_center_id,
        data_vencimento: data.due_date,
        forma_pagamento: data.payment_method,
        observacoes: data.notes
      }

      console.log('DEBUG: Dados sendo enviados para criar conta a pagar:', JSON.stringify(contaData, null, 2))
      const response = await api.post('/financeiro/contas-pagar', contaData)

      // Convert response back to frontend format
      const conta = response.data
      return {
        id: conta.id,
        supplier_id: conta.fornecedor_id,
        supplier_name: conta.fornecedor_nome || 'N/A',
        document_number: conta.numero_documento,
        description: conta.descricao,
        category_id: conta.categoria_id,
        category_name: conta.categoria_nome || 'N/A',
        cost_center_id: conta.centro_custo_id,
        original_amount: parseFloat(conta.valor_original || 0),
        outstanding_amount: parseFloat(conta.valor_pendente || conta.valor_original || 0),
        due_date: conta.data_vencimento,
        payment_date: conta.data_pagamento,
        status: conta.status,
        payment_method: conta.forma_pagamento,
        notes: conta.observacoes,
        created_at: conta.created_at,
        updated_at: conta.updated_at
      }
    } catch (error) {
      console.error('Error creating bill via financeiro API:', error)
      throw error
    }
  },

  updateBill: async (id: number, data: Partial<Bill>): Promise<Bill> => {
    try {
      // Convert frontend Bill format to backend conta_pagar format
      const contaData: any = {}
      if (data.supplier_id !== undefined) contaData.fornecedor_id = data.supplier_id
      if (data.document_number !== undefined) contaData.numero_documento = data.document_number
      if (data.description !== undefined) contaData.descricao = data.description
      if (data.category_id !== undefined) contaData.categoria_id = data.category_id
      if (data.cost_center_id !== undefined) contaData.centro_custo_id = data.cost_center_id
      if (data.original_amount !== undefined) contaData.valor_original = data.original_amount
      if (data.due_date !== undefined) contaData.data_vencimento = data.due_date
      if (data.payment_method !== undefined) contaData.forma_pagamento = data.payment_method
      if (data.notes !== undefined) contaData.observacoes = data.notes

      const response = await api.put(`/financeiro/contas-pagar/${id}`, contaData)

      // Convert response back to frontend format
      const conta = response.data
      return {
        id: conta.id,
        supplier_id: conta.fornecedor_id,
        supplier_name: conta.fornecedor_nome || 'N/A',
        document_number: conta.numero_documento,
        description: conta.descricao,
        category_id: conta.categoria_id,
        category_name: conta.categoria_nome || 'N/A',
        cost_center_id: conta.centro_custo_id,
        original_amount: parseFloat(conta.valor_original || 0),
        outstanding_amount: parseFloat(conta.valor_pendente || conta.valor_original || 0),
        due_date: conta.data_vencimento,
        payment_date: conta.data_pagamento,
        status: conta.status,
        payment_method: conta.forma_pagamento,
        notes: conta.observacoes,
        created_at: conta.created_at,
        updated_at: conta.updated_at
      }
    } catch (error) {
      console.error('Error updating bill via financeiro API:', error)
      throw error
    }
  },

  deleteBill: async (id: number): Promise<void> => {
    try {
      await api.delete(`/financeiro/contas-pagar/${id}`)
    } catch (error) {
      console.error('Error deleting bill via financeiro API:', error)
      throw error
    }
  },

  markAsPaid: async (id: number, paymentData: { payment_date: string, payment_method: string }): Promise<Bill> => {
    try {
      const response = await api.post(`/financeiro/contas-pagar/${id}/pagar`, {
        data_pagamento: paymentData.payment_date,
        forma_pagamento: paymentData.payment_method
      })

      // Convert response back to frontend format
      const conta = response.data
      return {
        id: conta.id,
        supplier_id: conta.fornecedor_id,
        supplier_name: conta.fornecedor_nome || 'N/A',
        document_number: conta.numero_documento,
        description: conta.descricao,
        category_id: conta.categoria_id,
        category_name: conta.categoria_nome || 'N/A',
        cost_center_id: conta.centro_custo_id,
        original_amount: parseFloat(conta.valor_original || 0),
        outstanding_amount: parseFloat(conta.valor_pendente || 0),
        due_date: conta.data_vencimento,
        payment_date: conta.data_pagamento,
        status: conta.status,
        payment_method: conta.forma_pagamento,
        notes: conta.observacoes,
        created_at: conta.created_at,
        updated_at: conta.updated_at
      }
    } catch (error) {
      console.error('Error marking bill as paid via financeiro API:', error)
      throw error
    }
  }
}

// Integrations API
export interface Integration {
  id: number
  name: string
  type: 'bank' | 'payment_gateway' | 'accounting' | 'crm' | 'other'
  provider: string
  status: 'active' | 'inactive' | 'error' | 'pending'
  last_sync: string
  auto_sync: boolean
  sync_frequency: number
  config: {
    api_key?: string
    api_secret?: string
    environment?: 'sandbox' | 'production'
    webhook_url?: string
    [key: string]: any
  }
  stats: {
    total_syncs: number
    last_sync_records: number
    total_records: number
    error_count: number
  }
  created_at: string
}

export interface IntegrationStats {
  total_integrations: number
  active_integrations: number
  total_synced_records: number
  sync_errors: number
  most_active_integration: string
  last_global_sync: string
}

export interface BankConnection {
  id: number
  bank_name: string
  connection_status: 'connected' | 'disconnected' | 'error'
  account_count: number
  last_transaction_sync: string
  connector_id: number
}

export const integrationsAPI = {
  getIntegrations: async (): Promise<Integration[]> => {
    // Como não há endpoint geral /integrations, vamos combinar dados dos diferentes tipos
    try {
      const [bankIntegrations, erpIntegrations] = await Promise.all([
        api.get('/integrations/banking'),
        api.get('/integrations/erp')
      ])

      const allIntegrations = [
        ...(bankIntegrations.data || []).map((item: any) => ({
          ...item,
          type: 'bank' as const
        })),
        ...(erpIntegrations.data || []).map((item: any) => ({
          ...item,
          type: 'accounting' as const
        }))
      ]

      return allIntegrations
    } catch (error) {
      console.error('Error fetching integrations:', error)
      return []
    }
  },

  getStats: async (): Promise<IntegrationStats> => {
    try {
      // Get integrations data to calculate stats
      const integrationsData = await integrationsAPI.getIntegrations()

      const stats: IntegrationStats = {
        total_integrations: integrationsData.length,
        active_integrations: integrationsData.filter((i: any) => i.status === 'active').length,
        total_synced_records: integrationsData.reduce((sum: number, i: any) => sum + (i.stats?.total_records || 0), 0),
        sync_errors: integrationsData.reduce((sum: number, i: any) => sum + (i.stats?.error_count || 0), 0),
        most_active_integration: integrationsData.length > 0 ?
          integrationsData.reduce((max: any, i: any) =>
            (i.stats?.total_syncs || 0) > (max.stats?.total_syncs || 0) ? i : max
          ).name : 'N/A',
        last_global_sync: integrationsData.length > 0 ?
          integrationsData.reduce((latest: string, i: any) => {
            const iDate = new Date(i.last_sync || 0)
            const latestDate = new Date(latest || 0)
            return iDate > latestDate ? i.last_sync : latest
          }, integrationsData[0].last_sync || new Date().toISOString()) :
          new Date().toISOString()
      }

      return stats
    } catch (error) {
      console.warn('Error calculating integration stats:', error)
      return {
        total_integrations: 0,
        active_integrations: 0,
        total_synced_records: 0,
        sync_errors: 0,
        most_active_integration: 'N/A',
        last_global_sync: new Date().toISOString()
      }
    }
  },

  getBankConnections: async (): Promise<BankConnection[]> => {
    const response = await api.get<BankConnection[]>('/integrations/banking')
    return response.data
  },

  createIntegration: async (data: Omit<Integration, 'id' | 'created_at' | 'stats'>): Promise<Integration> => {
    const response = await api.post<Integration>('/integrations', data)
    return response.data
  },

  updateIntegration: async (id: number, data: Partial<Integration>): Promise<Integration> => {
    const response = await api.put<Integration>(`/integrations/${id}`, data)
    return response.data
  },

  deleteIntegration: async (id: number): Promise<void> => {
    await api.delete(`/integrations/${id}`)
  },

  syncIntegration: async (id: number): Promise<any> => {
    const response = await api.post(`/integrations/${id}/sync`)
    return response.data
  },

  testIntegration: async (id: number): Promise<any> => {
    const response = await api.post(`/integrations/${id}/test`)
    return response.data
  }
}

// Chatbot API
export interface ChatMessage {
  id: number
  message: string
  response: string
  type: 'financial_query' | 'transaction_analysis' | 'general' | 'advice'
  confidence: number
  created_at: string
  user_id: number
  session_id?: string
}

export interface ChatSession {
  id: string
  title: string
  message_count: number
  last_message_at: string
  created_at: string
}

export interface ChatbotStats {
  total_conversations: number
  total_messages: number
  average_response_time: number
  satisfaction_rate: number
  most_common_topics: Array<{
    topic: string
    count: number
  }>
  today_messages: number
}

export interface ChatRequest {
  message: string
  session_id?: string
  context?: {
    current_page?: string
    user_data?: any
  }
}

export interface ChatResponse {
  response: string
  type: string
  confidence: number
  suggestions?: string[]
  actions?: Array<{
    type: string
    label: string
    data: any
  }>
  session_id: string
}

// Reconciliation Chat API - Conciliacao via Chat com IA
export interface ReconciliationChatResponse {
  success: boolean
  intent: string
  confidence: number
  response: string
  data?: any
  actions: Array<{ type: string; label: string }>
  suggestions: string[]
  requires_confirmation: boolean
  ai_provider: string
  timestamp: string
}

export const reconciliationChatAPI = {
  sendMessage: async (message: string, context?: any): Promise<ReconciliationChatResponse> => {
    const response = await api.post<ReconciliationChatResponse>('/reconciliation-chat/message', {
      message,
      context
    })
    return response.data
  },

  quickAction: async (action: string, params?: any): Promise<any> => {
    const response = await api.post('/reconciliation-chat/quick-action', {
      action,
      params
    })
    return response.data
  },

  getSuggestions: async (): Promise<any> => {
    const response = await api.get('/reconciliation-chat/suggestions')
    return response.data
  },

  getCommands: async (): Promise<any> => {
    const response = await api.get('/reconciliation-chat/commands')
    return response.data
  }
}

// WhatsApp Integration API
export const whatsappAPI = {
  linkPhone: async (phone: string): Promise<any> => {
    const response = await api.post('/whatsapp/link', { phone })
    return response.data
  },

  unlinkPhone: async (): Promise<any> => {
    const response = await api.delete('/whatsapp/link')
    return response.data
  },

  getStatus: async (): Promise<any> => {
    const response = await api.get('/whatsapp/status')
    return response.data
  },

  sendNotification: async (phone: string, notificationType: string, data: any): Promise<any> => {
    const response = await api.post('/whatsapp/notify', {
      phone,
      notification_type: notificationType,
      data
    })
    return response.data
  },

  testConnection: async (): Promise<any> => {
    const response = await api.post('/whatsapp/test')
    return response.data
  }
}

export const chatbotAPI = {
  sendMessage: async (data: ChatRequest): Promise<ChatResponse> => {
    const response = await api.post<ChatResponse>('/chatbot/chat', data)
    return response.data
  },

  getMessages: async (sessionId?: string): Promise<ChatMessage[]> => {
    const params = sessionId ? `?session_id=${sessionId}` : ''
    const response = await api.get<ChatMessage[]>(`/chatbot/messages${params}`)
    return response.data
  },

  getSessions: async (): Promise<ChatSession[]> => {
    const response = await api.get<ChatSession[]>('/chatbot/sessions')
    return response.data
  },

  getStats: async (): Promise<ChatbotStats> => {
    const response = await api.get<ChatbotStats>('/chatbot/stats')
    return response.data
  },

  createSession: async (title?: string): Promise<ChatSession> => {
    const response = await api.post<ChatSession>('/chatbot/sessions', { title })
    return response.data
  },

  deleteSession: async (sessionId: string): Promise<void> => {
    await api.delete(`/chatbot/sessions/${sessionId}`)
  },

  getFinancialAdvice: async (query: string): Promise<ChatResponse> => {
    const response = await api.post<ChatResponse>('/chatbot/advice', { query })
    return response.data
  },

  analyzeTransaction: async (transactionData: any): Promise<ChatResponse> => {
    const response = await api.post<ChatResponse>('/chatbot/analyze-transaction', { transaction: transactionData })
    return response.data
  }
}

// Cost Centers API - Real backend implementation
export interface CostCenter {
  id: number
  tenant_id: number
  name: string
  code?: string
  parent_id?: number
  manager_name?: string
  manager_email?: string
  budget_monthly?: number
  budget_yearly?: number
  description?: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface CostCenterStats {
  total_cost_centers: number
  total_budget: number
  total_spent: number
  active_cost_centers: number
  budget_utilization: number
  top_cost_centers: Array<{
    name: string
    spent: number
    budget: number
  }>
}

export const costCentersAPI = {
  getCostCenters: async (activeOnly: boolean = true, parentId?: number): Promise<CostCenter[]> => {
    // Use unified financial module first, fallback to old endpoint
    try {
      const unifiedCostCenters = await financeiroAPI.centrosCusto.list()
      return unifiedCostCenters
    } catch (error) {
      console.warn('Unified cost centers endpoint failed, using legacy endpoint')
      const params = new URLSearchParams()
      if (activeOnly !== undefined) params.append('active_only', activeOnly.toString())
      if (parentId !== undefined) params.append('parent_id', parentId.toString())

      const queryString = params.toString() ? `?${params.toString()}` : ''
      const response = await api.get<CostCenter[]>(`/cost-centers/${queryString}`)
      return response.data
    }
  },

  getStats: async (): Promise<CostCenterStats> => {
    try {
      // Calculate stats from cost centers data since backend doesn't have stats endpoint yet
      const costCenters = await costCentersAPI.getCostCenters(false) // Get all including inactive

      const stats: CostCenterStats = {
        total_cost_centers: costCenters.length,
        total_budget: costCenters.reduce((sum, cc) => sum + (cc.budget_yearly || 0), 0),
        total_spent: 0, // Would need transaction data to calculate
        active_cost_centers: costCenters.filter(cc => cc.is_active).length,
        budget_utilization: 0, // Would need spend data
        top_cost_centers: costCenters
          .filter(cc => cc.budget_yearly && cc.budget_yearly > 0)
          .sort((a, b) => (b.budget_yearly || 0) - (a.budget_yearly || 0))
          .slice(0, 5)
          .map(cc => ({
            name: cc.name,
            spent: 0, // Would need transaction data
            budget: cc.budget_yearly || 0
          }))
      }

      return stats
    } catch (error) {
      console.warn('Error calculating cost center stats:', error)
      return {
        total_cost_centers: 0,
        total_budget: 0,
        total_spent: 0,
        active_cost_centers: 0,
        budget_utilization: 0,
        top_cost_centers: []
      }
    }
  },

  createCostCenter: async (data: Omit<CostCenter, 'id' | 'tenant_id' | 'created_at' | 'updated_at'>): Promise<CostCenter> => {
    const response = await api.post<CostCenter>('/cost-centers/', data)
    return response.data
  },

  updateCostCenter: async (id: number, data: Partial<CostCenter>): Promise<CostCenter> => {
    const response = await api.put<CostCenter>(`/cost-centers/${id}`, data)
    return response.data
  },

  deleteCostCenter: async (id: number): Promise<void> => {
    await api.delete(`/cost-centers/${id}`)
  }
}

// Projects API - Real backend implementation
export interface Project {
  id: number
  tenant_id: number
  name: string
  code?: string
  client_name?: string
  start_date?: string
  end_date?: string
  estimated_duration?: number
  budget_amount?: number
  actual_cost?: number
  actual_revenue?: number
  manager_name?: string
  manager_email?: string
  status: string
  priority: string
  description?: string
  objectives?: string
  deliverables?: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface ProjectStats {
  total_projects: number
  active_projects: number
  completed_projects: number
  total_budget: number
  total_spent: number
  total_revenue: number
  profit_margin: number
  on_time_projects: number
  delayed_projects: number
  projects_by_status: Array<{
    status: string
    count: number
  }>
}

export const projectsAPI = {
  getProjects: async (activeOnly: boolean = true, status?: string): Promise<Project[]> => {
    const params = new URLSearchParams()
    if (activeOnly !== undefined) params.append('active_only', activeOnly.toString())
    if (status) params.append('status', status)

    const queryString = params.toString() ? `?${params.toString()}` : ''
    const response = await api.get<Project[]>(`/projects/${queryString}`)
    return response.data
  },

  getStats: async (): Promise<ProjectStats> => {
    try {
      // Calculate stats from projects data since backend doesn't have stats endpoint yet
      const projects = await projectsAPI.getProjects(false) // Get all including inactive

      const stats: ProjectStats = {
        total_projects: projects.length,
        active_projects: projects.filter(p => p.is_active && p.status !== 'completed').length,
        completed_projects: projects.filter(p => p.status === 'completed').length,
        total_budget: projects.reduce((sum, p) => sum + (p.budget_amount || 0), 0),
        total_spent: projects.reduce((sum, p) => sum + (p.actual_cost || 0), 0),
        total_revenue: projects.reduce((sum, p) => sum + (p.actual_revenue || 0), 0),
        profit_margin: 0, // Calculate after getting revenue and cost
        on_time_projects: projects.filter(p => {
          if (!p.end_date || p.status !== 'completed') return false
          // Would need to compare with actual completion date
          return true
        }).length,
        delayed_projects: projects.filter(p => {
          if (!p.end_date) return false
          const endDate = new Date(p.end_date)
          const today = new Date()
          return endDate < today && p.status !== 'completed'
        }).length,
        projects_by_status: Object.entries(
          projects.reduce((acc: Record<string, number>, p) => {
            acc[p.status] = (acc[p.status] || 0) + 1
            return acc
          }, {})
        ).map(([status, count]) => ({ status, count }))
      }

      // Calculate profit margin
      if (stats.total_revenue > 0) {
        stats.profit_margin = ((stats.total_revenue - stats.total_spent) / stats.total_revenue) * 100
      }

      return stats
    } catch (error) {
      console.warn('Error calculating project stats:', error)
      return {
        total_projects: 0,
        active_projects: 0,
        completed_projects: 0,
        total_budget: 0,
        total_spent: 0,
        total_revenue: 0,
        profit_margin: 0,
        on_time_projects: 0,
        delayed_projects: 0,
        projects_by_status: []
      }
    }
  },

  createProject: async (data: Omit<Project, 'id' | 'tenant_id' | 'created_at' | 'updated_at'>): Promise<Project> => {
    const response = await api.post<Project>('/projects/', data)
    return response.data
  },

  updateProject: async (id: number, data: Partial<Project>): Promise<Project> => {
    const response = await api.put<Project>(`/projects/${id}`, data)
    return response.data
  },

  deleteProject: async (id: number): Promise<void> => {
    await api.delete(`/projects/${id}`)
  }
}

// Cash Flow API - Based on transactions since no specific cash flow endpoints exist
// Cash Flow Entry (from /cash-flow/entries)
export interface CashFlowEntry {
  id: string
  date: string
  description: string
  amount: number
  type: 'income' | 'expense'
  status: 'confirmed' | 'projected' | 'pending'
  category: string
  account: string
  project_name?: string | null
  cost_center_name?: string | null
  is_recurring: boolean
  source: 'bill' | 'transaction'
  source_id: number
}

// Cash Flow Summary (from /cash-flow/summary)
export interface CashFlowSummary {
  period: string
  opening_balance: number
  total_income: number
  total_expenses: number
  income_confirmed: number
  income_projected: number
  expenses_confirmed: number
  expenses_projected: number
  net_flow: number
  closing_balance: number
  projected_balance: number
  current_balance: number
}

// Cash Flow Analysis (from /cash-flow/analysis)
export interface CashFlowAnalysis {
  current_balance: number
  month_flow: number
  burn_rate: number
  runway_days: number
  trend: 'positive' | 'negative' | 'neutral'
  month_income: number
  month_expenses: number
  last_month_flow: number
  projected_income_30d: number
  projected_expenses_30d: number
  projected_balance_30d: number
  analysis_date: string
}

export const cashFlowAPI = {
  /**
   * 📊 Busca movimentações de fluxo de caixa (entries)
   * Endpoint: GET /api/v1/cash-flow/entries
   */
  getEntries: async (
    startDate?: string,
    endDate?: string,
    filters?: {
      account_id?: number
      category_id?: number
      project_id?: number
      cost_center_id?: number
    }
  ): Promise<CashFlowEntry[]> => {
    try {
      const params = new URLSearchParams()
      if (startDate) params.append('start_date', startDate)
      if (endDate) params.append('end_date', endDate)
      if (filters?.account_id) params.append('account_id', filters.account_id.toString())
      if (filters?.category_id) params.append('category_id', filters.category_id.toString())
      if (filters?.project_id) params.append('project_id', filters.project_id.toString())
      if (filters?.cost_center_id) params.append('cost_center_id', filters.cost_center_id.toString())

      const response = await api.get(`/cash-flow/entries?${params.toString()}`)
      return response.data
    } catch (error) {
      console.error('Error fetching cash flow entries:', error)
      return []
    }
  },

  /**
   * 💰 Busca resumo consolidado do fluxo de caixa
   * Endpoint: GET /api/v1/cash-flow/summary
   */
  getSummary: async (startDate?: string, endDate?: string): Promise<CashFlowSummary> => {
    try {
      const params = new URLSearchParams()
      if (startDate) params.append('start_date', startDate)
      if (endDate) params.append('end_date', endDate)

      const response = await api.get(`/cash-flow/summary?${params.toString()}`)
      return response.data
    } catch (error) {
      console.error('Error fetching cash flow summary:', error)
      return {
        period: '',
        opening_balance: 0,
        total_income: 0,
        total_expenses: 0,
        income_confirmed: 0,
        income_projected: 0,
        expenses_confirmed: 0,
        expenses_projected: 0,
        net_flow: 0,
        closing_balance: 0,
        projected_balance: 0,
        current_balance: 0
      }
    }
  },

  /**
   * 📈 Busca análise avançada de fluxo de caixa
   * Endpoint: GET /api/v1/cash-flow/analysis
   */
  getAnalysis: async (): Promise<CashFlowAnalysis> => {
    try {
      const response = await api.get('/cash-flow/analysis')
      return response.data
    } catch (error) {
      console.error('Error fetching cash flow analysis:', error)
      return {
        current_balance: 0,
        month_flow: 0,
        burn_rate: 0,
        runway_days: 0,
        trend: 'neutral',
        month_income: 0,
        month_expenses: 0,
        last_month_flow: 0,
        projected_income_30d: 0,
        projected_expenses_30d: 0,
        projected_balance_30d: 0,
        analysis_date: new Date().toISOString()
      }
    }
  },

  /**
   * 📊 Busca fluxo por categoria
   * Endpoint: GET /api/v1/cash-flow/by-category
   */
  getByCategory: async (startDate?: string, endDate?: string) => {
    try {
      const params = new URLSearchParams()
      if (startDate) params.append('start_date', startDate)
      if (endDate) params.append('end_date', endDate)

      const response = await api.get(`/cash-flow/by-category?${params.toString()}`)
      return response.data
    } catch (error) {
      console.error('Error fetching cash flow by category:', error)
      return {
        period: '',
        income_by_category: [],
        expenses_by_category: []
      }
    }
  },

  /**
   * 📅 Busca fluxo diário
   * Endpoint: GET /api/v1/cash-flow/daily-flow
   */
  getDailyFlow: async (startDate?: string, endDate?: string) => {
    try {
      const params = new URLSearchParams()
      if (startDate) params.append('start_date', startDate)
      if (endDate) params.append('end_date', endDate)

      const response = await api.get(`/cash-flow/daily-flow?${params.toString()}`)
      return response.data
    } catch (error) {
      console.error('Error fetching daily cash flow:', error)
      return {
        period: '',
        opening_balance: 0,
        daily_flow: []
      }
    }
  },

  /**
   * ⚠️ Busca períodos críticos
   * Endpoint: GET /api/v1/cash-flow/critical-periods
   */
  getCriticalPeriods: async (daysAhead: number = 90) => {
    try {
      const response = await api.get(`/cash-flow/critical-periods?days_ahead=${daysAhead}`)
      return response.data
    } catch (error) {
      console.error('Error fetching critical periods:', error)
      return {
        current_balance: 0,
        projection_days: daysAhead,
        critical_periods_count: 0,
        critical_periods: [],
        has_critical_periods: false
      }
    }
  }
}

// Legacy compatibility
export interface LoginCredentials {
  email: string
  password: string
}

export interface AuthResponse {
  access_token: string
  token_type: string
  user: {
    id: string
    email: string
    name: string
    is_active: boolean
    created_at: string
  }
}

export interface DashboardData {
  total_balance: number
  total_income: number
  total_expenses: number
  transactions_count: number
  recent_transactions: Transaction[]
}

export class FinProAPI {
  static async login(credentials: LoginCredentials): Promise<AuthResponse> {
    return authAPI.login(credentials) as any
  }

  static async getDashboard(): Promise<DashboardData> {
    return dashboardAPI.getMetrics() as any
  }

  static async getTransactions(): Promise<Transaction[]> {
    return transactionsAPI.list()
  }

  static async createTransaction(data: Partial<Transaction>): Promise<Transaction> {
    return transactionsAPI.create(data as CreateTransactionRequest)
  }

  static async updateTransaction(id: string, data: Partial<Transaction>): Promise<Transaction> {
    return transactionsAPI.update(id, data)
  }

  static async deleteTransaction(id: string): Promise<void> {
    return transactionsAPI.delete(id)
  }
}

// Pluggy Integration API
export interface PluggyConnector {
  id: number
  name: string
  institution_url: string
  image_url: string
  primary_color: string
  type: string
  country: string
  credentials: any[]
  has_mfa: boolean
}

export interface PluggyConnection {
  id: number
  tenant_id: number
  connector_id: number
  item_id: string
  status: 'active' | 'updating' | 'login_error' | 'outdated' | 'error'
  error_message?: string
  last_sync_at?: string
  created_at: string
  connector?: PluggyConnector
}

export interface PluggyAccount {
  id: number
  connection_id: number
  account_id: string
  type: string
  subtype: string
  name: string
  number: string
  balance: {
    current: number
    available: number
  }
  currency_code: string
  created_at: string
}

export interface PluggyTransaction {
  id: string
  description: string
  amount: number
  date: string
  balance: number
  category: string
  type: 'DEBIT' | 'CREDIT'
}

export interface PluggySettings {
  id: number
  tenant_id: number
  client_id: string
  environment: 'sandbox' | 'production'
  is_configured: boolean
  is_active: boolean
  last_test_status?: 'success' | 'error'
  last_test_error?: string
  last_test_at?: string
  created_at: string
}

export interface PluggySettingsCreate {
  client_id: string
  client_secret: string
  environment: 'sandbox' | 'production'
}

export interface PluggySettingsTestResponse {
  success: boolean
  message: string
  error?: string
  connectors_count?: number
}

export const pluggyAPI = {
  // Settings endpoints
  getSettings: async (): Promise<PluggySettings> => {
    const response = await api.get('/integrations/pluggy/settings')
    return response.data
  },

  saveSettings: async (data: PluggySettingsCreate): Promise<PluggySettings> => {
    const response = await api.post('/integrations/pluggy/settings', data)
    return response.data
  },

  testSettings: async (): Promise<PluggySettingsTestResponse> => {
    const response = await api.post('/integrations/pluggy/settings/test')
    return response.data
  },

  // Listar bancos disponíveis
  getConnectors: async (country: string = 'BR'): Promise<PluggyConnector[]> => {
    const response = await api.get(`/integrations/pluggy/connectors?country=${country}`)
    return response.data
  },

  // Criar token de conexão
  createConnectToken: async (connectorId?: number): Promise<{ connect_token: string }> => {
    const params = connectorId ? { connector_id: connectorId } : {}
    const response = await api.post('/integrations/pluggy/connect-token', null, {
      params
    })
    return response.data
  },

  // Criar nova conexão bancária
  createConnection: async (data: {
    connector_id?: number
    item_id: string
    auto_sync?: boolean
    sync_frequency?: number
    webhook_url?: string | null
  }): Promise<PluggyConnection> => {
    // Extrair item_id para enviar apenas como query param, não no body
    const { item_id, ...bodyData } = data
    const response = await api.post('/integrations/pluggy/connections', bodyData, {
      params: { item_id }
    })
    return response.data
  },

  // Listar conexões
  getConnections: async (activeOnly: boolean = true): Promise<PluggyConnection[]> => {
    const response = await api.get('/integrations/pluggy/connections', {
      params: { active_only: activeOnly }
    })
    return response.data
  },

  // Obter contas de uma conexão
  getConnectionAccounts: async (
    connectionId: number,
    syncFirst: boolean = false
  ): Promise<PluggyAccount[]> => {
    const response = await api.get(
      `/integrations/pluggy/connections/${connectionId}/accounts`,
      { params: { sync_first: syncFirst } }
    )
    return response.data
  },

  // Sincronizar conexão
  syncConnection: async (connectionId: number): Promise<{
    success: boolean
    message: string
    accounts_synced: number
    transactions_synced: number
  }> => {
    const response = await api.post(
      `/integrations/pluggy/connections/${connectionId}/sync`
    )
    return response.data
  },

  // Sincronizar transações de uma conta
  syncAccountTransactions: async (
    accountId: number,
    fromDate?: string,
    toDate?: string
  ): Promise<{
    success: boolean
    transactions_imported: number
    period: { from: string, to: string }
  }> => {
    const params: any = {}
    if (fromDate) params.from_date = fromDate
    if (toDate) params.to_date = toDate

    const response = await api.post(
      `/integrations/pluggy/accounts/${accountId}/sync-transactions`,
      null,
      { params }
    )
    return response.data
  },

  // Deletar conexão
  deleteConnection: async (connectionId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/integrations/pluggy/connections/${connectionId}`)
    return response.data
  }
}

// ==========================================
// HUBNAI NATIVE PAYMENT GATEWAY API
// ==========================================

export interface HubnaiCredentials {
  id?: number
  active: boolean
  sandbox: boolean
  last_test_success?: boolean
  last_tested_at?: string
}

export interface HubnaiBalance {
  available: number
  pending: number
  blocked: number
  total: number
  updated_at: string
}

export interface HubnaiCharge {
  id: number
  hubnai_charge_id: string
  amount: number
  payment_method: 'boleto' | 'pix' | 'credit_card' | 'debit_card'
  status: 'pending' | 'paid' | 'failed' | 'cancelled' | 'expired'
  customer_name?: string
  customer_email?: string
  customer_document?: string
  due_date?: string
  payment_date?: string
  boleto_url?: string
  pix_qr_code_url?: string
  pix_code?: string
  expires_at?: string
  created_at: string
}

export interface HubnaiCustomer {
  id: number
  hubnai_customer_id: string
  name: string
  email: string
  document: string
  phone?: string
  kyc_status: 'pending' | 'approved' | 'rejected'
  created_at: string
}

export interface HubnaiTransfer {
  id: number
  hubnai_transfer_id: string
  amount: number
  fee_amount: number
  net_amount: number
  transfer_type: 'ted' | 'pix'
  status: 'pending' | 'processing' | 'completed' | 'failed'
  bank_account: {
    bank_code: string
    agency: string
    account: string
    account_type: 'checking' | 'savings'
  }
  requested_at: string
  estimated_date?: string
  completed_at?: string
}

export interface HubnaiStatistics {
  total_charges: number
  total_charges_paid: number
  total_charges_pending: number
  total_charges_failed: number
  total_amount_received: number
  total_amount_pending: number
  total_fees_paid: number
  total_transfers: number
  total_transfers_completed: number
  average_charge_value: number
  period_start: string
  period_end: string
}

export const hubnaiAPI = {
  // ===== CREDENTIALS =====

  saveCredentials: async (credentials: {
    api_key: string
    api_secret: string
    sandbox: boolean
  }): Promise<HubnaiCredentials> => {
    const response = await api.post('/hubnai/credentials', credentials)
    return response.data
  },

  getCredentials: async (): Promise<HubnaiCredentials> => {
    const response = await api.get('/hubnai/credentials')
    return response.data
  },

  testCredentials: async (credentials?: {
    api_key: string
    api_secret: string
    sandbox: boolean
  }): Promise<{
    success: boolean
    message: string
    balance?: HubnaiBalance
  }> => {
    const response = await api.post('/hubnai/credentials/test', credentials || {})
    return response.data
  },

  deleteCredentials: async (): Promise<{ message: string }> => {
    const response = await api.delete('/hubnai/credentials')
    return response.data
  },

  // ===== BALANCE =====

  getBalance: async (): Promise<HubnaiBalance> => {
    const response = await api.get('/hubnai/balance')
    return response.data
  },

  // ===== CHARGES =====

  listCharges: async (params?: {
    status?: string
    payment_method?: string
    start_date?: string
    end_date?: string
    limit?: number
    skip?: number
  }): Promise<{ data: HubnaiCharge[], total_count: number, has_more: boolean }> => {
    const queryParams = new URLSearchParams()
    if (params?.status) queryParams.append('status', params.status)
    if (params?.payment_method) queryParams.append('payment_method', params.payment_method)
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.skip) queryParams.append('skip', params.skip.toString())

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
    const response = await api.get(`/hubnai/charges${queryString}`)
    return response.data
  },

  // ===== PIX CHARGES =====

  createPixCharge: async (data: {
    amount: number  // em centavos
    orderId: string
    expiration?: string
    description?: string
    customer?: {
      name: string
      documentNumber: string
      email: string
      address?: Record<string, any>
    }
  }): Promise<{
    status: number
    returnCode: string
    returnMessage: string
    paymentId: string
    orderNumber: string
    type: string
    qrCodeContent: string
    qrCodeUrl: string
    dueDate?: string
    amount: number
    liquid: number
    fees: number
    acquirer: string
    createdAt: string
    customer?: Record<string, any>
  }> => {
    // Gerar UUID para o payment_id (conforme backend)
    const paymentId = crypto.randomUUID()
    const response = await api.get(`/hubnai/charges/pix/${paymentId}`, {
      params: data
    })
    return response.data
  },

  refundPixCharge: async (paymentId: string, data: {
    amount: number  // em centavos
    returnReasonCode: 'MD06' | 'FR01' | 'BE08'
  }): Promise<{
    status: number
    returnCode: string
    returnMessage: string
    paymentId: string
    orderNumber: string
    type: string
    amount: number
    releaseAt: string
  }> => {
    const response = await api.post(`/hubnai/charges/pix/${paymentId}/refund`, data)
    return response.data
  },

  // ===== CARD PAYMENTS =====

  createCardPayment: async (data: {
    amount: number  // em centavos
    order_id?: string
    payment: {
      type: 'credit' | 'debit'
      installments?: number
      capture?: boolean
      has_interest?: boolean
      softdescriptor?: string
      card?: {
        holder: string
        number: string
        expiryMonth: string
        expiryYear: string
        cvv: string
      }
      vault?: string
    }
    customer: {
      name: string
      document: string
      email?: string
      phone?: string
      birthDate?: string
      ip?: string
    }
  }): Promise<{
    id: string
    status: number
    returnCode: string
    returnMessage: string
    authorizationCode?: string
    orderNumber?: string
    nsu?: string
    amount: number
    installments: number
    type: string
    brand?: string
    createdAt: string
  }> => {
    const response = await api.post('/hubnai/payments/card', data)
    return response.data
  },

  captureCardPayment: async (paymentId: string, data: {
    amount: number  // em centavos
  }): Promise<Record<string, any>> => {
    const response = await api.post(`/hubnai/payments/card/${paymentId}/capture`, data)
    return response.data
  },

  cancelCardPayment: async (paymentId: string): Promise<Record<string, any>> => {
    const response = await api.post(`/hubnai/payments/card/${paymentId}/cancel`)
    return response.data
  },

  getCardPayment: async (paymentId: string): Promise<Record<string, any>> => {
    const response = await api.get(`/hubnai/payments/card/${paymentId}`)
    return response.data
  },

  listCardPayments: async (params?: {
    page?: number
    perPage?: number
    dateStart?: string
    dateEnd?: string
  }): Promise<{
    data: Record<string, any>[]
    total: number
    page: number
    perPage: number
  }> => {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.perPage) queryParams.append('perPage', params.perPage.toString())
    if (params?.dateStart) queryParams.append('dateStart', params.dateStart)
    if (params?.dateEnd) queryParams.append('dateEnd', params.dateEnd)

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
    const response = await api.get(`/hubnai/payments/card${queryString}`)
    return response.data
  },

  // ===== CUSTOMERS (KYC) =====

  listCustomers: async (params?: {
    limit?: number
    skip?: number
  }): Promise<HubnaiCustomer[]> => {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.skip) queryParams.append('skip', params.skip.toString())

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
    const response = await api.get(`/hubnai/customers${queryString}`)
    return response.data
  },

  getCustomer: async (customerId: number): Promise<HubnaiCustomer> => {
    const response = await api.get(`/hubnai/customers/${customerId}`)
    return response.data
  },

  createCustomer: async (data: {
    name: string
    email: string
    document: string
    phone?: string
    birth_date?: string
    address?: {
      street: string
      number: string
      complement?: string
      neighborhood: string
      city: string
      state: string
      zipcode: string
    }
  }): Promise<HubnaiCustomer> => {
    const response = await api.post('/hubnai/customers', data)
    return response.data
  },

  // ===== TRANSFERS (SAQUES) =====

  listTransfers: async (params?: {
    status?: string
    limit?: number
    skip?: number
  }): Promise<{ data: HubnaiTransfer[], total_count: number, has_more: boolean }> => {
    const queryParams = new URLSearchParams()
    if (params?.status) queryParams.append('status', params.status)
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.skip) queryParams.append('skip', params.skip.toString())

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
    const response = await api.get(`/hubnai/transfers${queryString}`)
    return response.data
  },

  createTransfer: async (data: {
    amount: number
    transfer_type: 'ted' | 'pix'
    bank_account: {
      bank_code: string
      agency: string
      account: string
      account_type: 'checking' | 'savings'
      document: string
      name: string
    }
    description?: string
  }): Promise<HubnaiTransfer> => {
    const response = await api.post('/hubnai/transfers', data)
    return response.data
  },

  // ===== STATISTICS =====

  getStatistics: async (params?: {
    start_date?: string
    end_date?: string
  }): Promise<HubnaiStatistics> => {
    const queryParams = new URLSearchParams()
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
    const response = await api.get(`/hubnai/statistics${queryString}`)
    return response.data
  },

  // ===== SYNC =====

  syncData: async (params?: {
    start_date?: string
    end_date?: string
  }): Promise<{
    success: boolean
    message: string
    charges_synced: number
    customers_synced: number
    transfers_synced: number
  }> => {
    const queryParams = new URLSearchParams()
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
    const response = await api.post(`/hubnai/sync${queryString}`)
    return response.data
  }
}

export default api